import { makeAutoObservable } from "mobx";
import { RootStore } from "./RootStore";
import {
  CANAL_DEL_TEMA,
  buildTheme,
  isPreviewAudience,
  Mode,
  ThemeDraft,
} from "../theme/buildTheme";
import { Storage } from "@react-force/web-utils";
import { LsKeys } from "./LsKeys";
import { PROJECT_TIME_ZONE } from "../config/projectTimeZone";
import { API_URL } from "../api/apiClient";

const DEFAULT_MODE: Mode = "light";

function esModo(value: unknown): value is Mode {
  return value === "light" || value === "dark";
}

/**
 * El borrador del banco, si quedo uno abierto. Se lee de forma defensiva: un
 * borrador corrupto no puede impedir que la aplicacion arranque, asi que ante
 * cualquier duda se descarta.
 */
function readDraft(): ThemeDraft | null {
  try {
    const raw = sessionStorage.getItem(LsKeys.themeDraft);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as ThemeDraft) : null;
  } catch {
    return null;
  }
}

/** La eleccion hecha en este dispositivo, si la hubo. */
export function deviceThemeChoice(): Mode | null {
  const choice = Storage.get(LsKeys.themeChoice, null);
  return esModo(choice) ? choice : null;
}

/**
 * Con que modo abre la aplicacion, en orden de precedencia:
 *
 *   1. lo que el usuario eligio EN ESTE dispositivo
 *   2. su ultima eleccion, que llega con el perfil despues del login
 *   3. el default del proyecto
 *
 * El paso 1 se resuelve aca, sincronico, y por eso no hay destello: la app ya
 * abre en el modo correcto en vez de saltar cuando llega el perfil.
 */
function initialMode(): Mode {
  return deviceThemeChoice() ?? DEFAULT_MODE;
}

/**
 * Si `value` tiene la forma minima de un `ThemeDraft`: los cuatro bloques
 * presentes y como objeto. No valida mas adentro — `themeOptions` ya
 * tolera un borrador parcial o con ruido, y pedir mas acá duplicaria esa
 * tolerancia sin ganar nada.
 */
export function esFormaDeTema(value: unknown): value is ThemeDraft {
  if (!value || typeof value !== "object") return false;
  const t = value as Record<string, unknown>;
  return (
    !!t.light &&
    typeof t.light === "object" &&
    !!t.dark &&
    typeof t.dark === "object" &&
    !!t.primitives &&
    typeof t.primitives === "object" &&
    !!t.components &&
    typeof t.components === "object"
  );
}

/** El tema aplicado, si quedo uno guardado. Defensivo por la misma razon que readDraft. */
function readAppliedDraft(): ThemeDraft | null {
  try {
    const raw = localStorage.getItem(LsKeys.appliedTheme);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return esFormaDeTema(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export class PrefStore {
  private mainMenuState: Record<string, boolean> = {};
  currentContentTop: number = 0;
  rootStore: RootStore;
  paletteType: Mode = initialMode();
  sideBarOpen = true;
  toolbarSize: "small" | "medium" = "medium";
  thousandSeparator: "," | "." = ".";
  decimalSeparator: "," | "." = ",";
  autoComplete = "one-time-code";
  controlWidth = 500;
  mainMenuSelectedId = "";
  lastClickedMenu = "";
  // La preferencia del usuario; por defecto, la zona del proyecto.
  timezone = PROJECT_TIME_ZONE;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.loadAppliedTheme();
    this.escucharDifusion();
  }

  setSideBarOpen(sideBarOpen: boolean) {
    this.sideBarOpen = sideBarOpen;
  }

  setLastClickedMenu(menuItemId: string) {
    this.lastClickedMenu = menuItemId;
  }

  toggleMenuState(id: string) {
    this.mainMenuState[id] = !this.mainMenuState[id];
  }

  setCurrentContentTop(value: number) {
    this.currentContentTop = value;
  }

  getCurrentContentTop() {
    return this.currentContentTop;
  }

  getMenuState(id: string) {
    return !!this.mainMenuState[id];
  }

  setMainMenuSelectedId(id: string) {
    this.mainMenuSelectedId = id;
  }

  // ----- Actions -----
  /**
   * Aplica un modo SIN registrarlo como eleccion del dispositivo. Es lo que usa
   * el perfil al llegar: sembrar un dispositivo que todavia no eligio.
   */
  setTheme(theme: Mode) {
    this.paletteType = theme;
  }

  /**
   * El usuario eligio. Se aplica y queda anotado en el dispositivo, que a
   * partir de ahora manda sobre lo que diga el perfil.
   */
  chooseTheme(theme: Mode) {
    this.paletteType = theme;
    Storage.set(LsKeys.themeChoice, theme);
  }

  /** Si este dispositivo ya tiene una eleccion propia, el perfil no la pisa. */
  get hasDeviceThemeChoice() {
    return deviceThemeChoice() !== null;
  }

  /**
   * El borrador del banco de diseno. Nulo en el uso normal.
   *
   * Se reemplaza ENTERO en cada edicion, nunca se muta por dentro: el getter
   * del tema es un `computed` de MobX y solo se entera de un cambio si la
   * referencia cambia. Mutar una propiedad anidada dejaria la pantalla con el
   * tema viejo hasta que algo mas la invalide, que es un sintoma muy dificil de
   * atribuir.
   *
   * Vive en sessionStorage y no en localStorage a proposito: es trabajo en
   * curso de una sesion de diseno, no una preferencia. Lo que se conserva es su
   * exportacion al bloque `theme` de la configuracion.
   */
  themeDraft: ThemeDraft | null = readDraft();

  setThemeDraft(draft: ThemeDraft | null) {
    this.themeDraft = draft;
    // sessionStorage directo y no el helper Storage, que respalda en
    // localStorage: el borrador tiene que morir con la pestania.
    try {
      if (draft)
        sessionStorage.setItem(LsKeys.themeDraft, JSON.stringify(draft));
      else sessionStorage.removeItem(LsKeys.themeDraft);
    } catch {
      // Sin sessionStorage el banco sigue funcionando; solo no sobrevive a un refresco.
    }
  }

  /**
   * Escucha el tema que otra pestania esta probando, y lo pinta.
   *
   * Vive ACA y no en el laboratorio, y esa es toda la diferencia: el que escucha
   * tiene que ser lo que pinta el tema en CUALQUIER pantalla. Con el oyente
   * dentro del laboratorio, la difusion solo funcionaba entre dos pestanias de
   * /theme-lab —una pestania con la aplicacion abierta en otra pantalla no tenia
   * quien escuchara—, que es justo el caso que la difusion existe para resolver:
   * tener la app al lado y verla moverse con cada control.
   *
   * Se ignora un mensaje que traiga el borrador que ya esta puesto: el canal
   * entrega a todos los objetos del mismo nombre menos al que emitio, asi que la
   * propia pestania que movio el control tambien lo recibe.
   */
  private escucharDifusion() {
    try {
      if (typeof BroadcastChannel === "undefined") return;
      const canal = new BroadcastChannel(CANAL_DEL_TEMA);
      canal.addEventListener("message", (evento: MessageEvent) => {
        const mensaje = evento.data as { draft?: unknown } | null;
        if (!mensaje || typeof mensaje !== "object") return;
        const draft = (mensaje.draft ?? null) as ThemeDraft | null;
        if (JSON.stringify(draft) === JSON.stringify(this.themeDraft)) return;
        this.setThemeDraft(draft);
      });
    } catch {
      // Sin canal la aplicacion sigue andando: lo unico que se pierde es que las
      // otras pestanias se enteren. Mismo criterio que sessionStorage.
    }
  }

  /**
   * Descarta el borrador aplicado. Queda separado de `setThemeDraft(null)`
   * porque es la otra mitad del canal: aplicar y descartar un tema
   * persistido son las dos operaciones que necesita quien monte un tema
   * sobre `themeDraft` desde afuera del banco.
   */
  clearThemeDraft() {
    this.setThemeDraft(null);
  }

  /**
   * El tema activo que trajo el servidor, sembrado al arrancar. Vive en
   * localStorage y no en sessionStorage como `themeDraft`: esto no es
   * trabajo en curso de una sesion de diseno, es lo que el sistema decidio
   * para todos y tiene que sobrevivir a cerrar la pestania.
   */
  appliedDraft: ThemeDraft | null = readAppliedDraft();

  setAppliedDraft(draft: ThemeDraft | null) {
    this.appliedDraft = draft;
    try {
      if (draft)
        localStorage.setItem(LsKeys.appliedTheme, JSON.stringify(draft));
      else localStorage.removeItem(LsKeys.appliedTheme);
    } catch {
      // Sin localStorage el tema aplicado sigue funcionando en memoria por esta sesion.
    }
  }

  /**
   * Trae el tema activo de `/public/theme`, sin autenticar. `resolved` ya
   * viene con `light`/`dark`/`primitives`/`components` finales — la misma
   * forma que `ThemeDraft` —, asi que se proyecta directo, sin pasar por
   * nada del banco de diseno.
   *
   * Revalida lo que `readAppliedDraft` ya sembro sincronicamente, y distingue
   * TRES respuestas que no significan lo mismo:
   *
   *   - 404: el server AFIRMA que no hay tema aplicado. Se limpia el cache
   *     (`setAppliedDraft(null)`) y el arranque cae al tema generado. Sin
   *     esto, desactivar un tema en el server nunca le llega a quien ya lo
   *     tiene guardado en localStorage.
   *   - cualquier otro fallo (red, timeout, 5xx): el server no afirma nada
   *     sobre el tema, asi que el cache se CONSERVA. Es una salvedad
   *     deliberada sobre "la lectura falla, cae al generado": un usuario
   *     offline con un tema valido no tiene por que ver la app despintarse
   *     en cada corte de red — el salto de tema es cosa de la PRIMERA vez
   *     que una maquina lo ve, no de cada revalidacion.
   *   - 200 con un cuerpo que no tiene la forma esperada: no es "no hay
   *     tema" (eso es 404) ni un fallo de transporte — es una discrepancia
   *     de forma, y tambien se CONSERVA el cache. Silenciosa para quien usa
   *     la app (misma politica que un fallo), pero se deja un `console.error`
   *     de diagnostico para quien mira la consola — el mismo criterio que ya
   *     usa el endpoint del lado del server para su propio caso analogo.
   *
   * Se llama desde el constructor, asi que corre ANTES del login — el
   * endpoint tambien pinta esa pantalla.
   */
  async loadAppliedTheme() {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000);
      // no-store: esta lectura ES la revalidación posterior a aplicar un tema
      // nuevo. La cache HTTP de 60s del endpoint sirve a todos los DEMAS
      // fetches; a esta, servirle la respuesta vieja del arranque haria que
      // quien acaba de aplicar un tema siga viendo el anterior.
      const response = await fetch(`${API_URL}/public/theme`, {
        signal: controller.signal,
        cache: "no-store",
      });
      clearTimeout(timeout);
      if (response.status === 404) {
        this.setAppliedDraft(null);
        return;
      }
      if (!response.ok) return;
      const body = await response.json();
      if (esFormaDeTema(body?.resolved)) {
        this.setAppliedDraft(body.resolved);
      } else {
        console.error(
          "El tema recibido de /public/theme no tiene la forma esperada; se conserva el cache.",
          body
        );
      }
    } catch {
      // Sin servidor, timeout o red cortada: no se sabe nada nuevo, asi que
      // el cache existente se conserva tal cual.
    }
  }

  toggleTheme = () => {
    this.chooseTheme(this.paletteType === "light" ? "dark" : "light");
  };

  // El tema sale de src/theme/, construido desde los tokens declarados en la
  // configuracion del proyecto. Sigue siendo un `computed` de MobX sobre
  // `paletteType`: `buildTheme` es puro, asi que cambiar de modo recalcula y
  // nada mas lo hace.
  /**
   * Si a este usuario le toca el tema en pruebas. Se lee del usuario en sesion,
   * asi que el tema se recalcula solo al entrar y al salir.
   */
  get seesPreviewTheme() {
    return isPreviewAudience(this.rootStore?.authStore?.me as any);
  }

  /**
   * El borrador con el que la aplicacion esta pintando, con el del probador por encima del aplicado.
   *
   * Existe para que esa regla tenga UN solo lugar. No es solo `get theme()`
   * quien la necesita: la comparacion claro/oscuro de la galeria construye los
   * DOS modos con este mismo borrador, y escribir la expresion alla seria una
   * segunda fuente que se separa de esta sin hacer ruido.
   */
  get effectiveThemeDraft(): ThemeDraft | null {
    return this.themeDraft ?? this.appliedDraft;
  }

  /**
   * Construir el tema NO puede tirar, y por eso el try/catch esta aca y no en
   * un error boundary.
   *
   * Este getter se evalua durante el render de `App`, al armar el elemento
   * `<ThemeProvider theme={...}>`. Un boundary que `App` devuelva todavia no
   * existe en ese momento, asi que no lo atrapa: la excepcion sube hasta la
   * raiz y deja la pantalla en blanco. Y como el borrador vive en storage,
   * recargar tampoco lo arregla — queda tildada hasta borrarlo a mano.
   *
   * Ante el error se cae al tema sin borrador, que siempre se puede construir
   * porque sale de valores generados. La aplicacion queda usable y el
   * borrador se puede descartar con `clearThemeDraft()`.
   */
  get theme() {
    try {
      return buildTheme(
        this.paletteType,
        this.effectiveThemeDraft,
        this.seesPreviewTheme
      );
    } catch (e) {
      console.error(
        "El borrador de tema no se pudo aplicar; se usa el tema generado.",
        e
      );
      return buildTheme(this.paletteType, null, this.seesPreviewTheme);
    }
  }
}
